import type { ChatItem } from './types.js';
import { turnAnchorId } from './turn-nav.js';

/**
 * Bounds how much of a finalized transcript is reachable at once.
 *
 * A turn costs ~136 nodes, so mounting a 1,000-turn agent produced 136k nodes
 * and took ~10s to become readable. The initial window keeps first paint
 * bounded; once older rows are revealed or paged in, measured turn
 * virtualization keeps only the viewport neighborhood mounted behind
 * height-preserving spacers.
 *
 * Only the newest slice mounts on open; older history is one click away. The
 * cost of opening an agent is now set by the window, not by how long the agent
 * has been running.
 */

/**
 * Items mounted when a thread opens. Two items per turn (the prompt and the
 * response), so this is ~40 turns of immediately scrollable history — past
 * what a reader scrolls back through before reaching for the button, and a
 * ~5k-node mount instead of an unbounded one.
 */
export const INITIAL_VISIBLE_MESSAGES = 80;

/** Additional items mounted each time the reader asks for earlier history. */
export const REVEAL_STEP = 80;

/**
 * True when an item holds a question the agent is still waiting on. These must
 * stay mounted no matter how old they are: the card registers itself with the
 * host on mount, and that registration is what dims the transcript, disables
 * the composer, and lifts the prompt into the overlay. A windowed-out question
 * would leave the user typing past a turn that cannot proceed.
 */
function hasUnansweredPrompt(item: ChatItem): boolean {
  if (item.role !== 'assistant' || !('blocks' in item) || !item.blocks) return false;
  return item.blocks.some(
    (block) =>
      (block.type === 'control_request' || block.type === 'codex_approval') && !block.responded,
  );
}

export interface TranscriptWindow {
  /** The newest items, in transcript order. */
  visible: ChatItem[];
  /** How many older items are not mounted. */
  hiddenCount: number;
}

export interface TranscriptTurnGroup {
  /** Stable identity for the measured wrapper. */
  id: string;
  /** One user prompt and everything through its completed assistant response. */
  items: ChatItem[];
  /** Turn-start anchor carried by the stable wrapper through mount/spacer changes. */
  anchorId?: string;
  /** Durable event ids used to force-mount a highlighted group. */
  eventIds: string[];
  /** Pending approval/question groups must keep their registration effects mounted. */
  mustStayMounted: boolean;
}

/**
 * Decide whether local UI state makes an off-screen turn ineligible for
 * virtualization. Many task and log components are expanded by default, so
 * an open-state attribute alone is not evidence that the reader changed the
 * row. Focus is always state worth preserving; open UI is preserved only
 * after an interaction inside that turn.
 */
export function shouldRetainVirtualTurnUi({
  containsActiveElement,
  hasOpenUi,
  userInteracted,
}: {
  containsActiveElement: boolean;
  hasOpenUi: boolean;
  userInteracted: boolean;
}): boolean {
  return containsActiveElement || (userInteracted && hasOpenUi);
}

function sourceEventId(item: ChatItem): string | undefined {
  return 'sourceEvent' in item ? item.sourceEvent?.id : undefined;
}

function itemEventIds(item: ChatItem): string[] {
  const ids: string[] = [];
  const itemId = sourceEventId(item);
  if (itemId) ids.push(itemId);
  if ('blocks' in item) {
    for (const block of item.blocks ?? []) {
      if (block.sourceEvent?.id) ids.push(block.sourceEvent.id);
      // A log run is one block per many events. Each line is separately
      // addressable, so the whole run has to keep its group mounted — anchoring
      // only on the leader would send a deep link to line 2 down the
      // mount-everything fallback and still find nothing to scroll to.
      if (block.type === 'agent_log') {
        for (const line of block.lines) {
          if (line.sourceEvent?.id) ids.push(line.sourceEvent.id);
        }
      }
    }
  }
  return ids;
}

/**
 * Group transcript items at the user-prompt boundary so virtualization never
 * leaves half a completed turn mounted. Preamble/lifecycle rows remain small
 * standalone groups; a truncated window that starts on an assistant response
 * does too.
 */
export function groupTranscriptTurns(messages: ChatItem[]): TranscriptTurnGroup[] {
  const groups: TranscriptTurnGroup[] = [];
  let turn: ChatItem[] | null = null;

  const append = (items: ChatItem[]) => {
    const first = items[0];
    const prompt = first?.role === 'user' ? first : undefined;
    const eventIds = items.flatMap(itemEventIds);
    groups.push({
      id: prompt ? `turn:${prompt.id}` : `item:${first?.id ?? groups.length}`,
      items,
      anchorId: prompt ? turnAnchorId(prompt.id) : undefined,
      eventIds,
      mustStayMounted: items.some(hasUnansweredPrompt),
    });
  };

  for (const item of messages) {
    if (item.role === 'user') {
      if (turn) append(turn);
      turn = [item];
      continue;
    }
    if (turn) {
      turn.push(item);
      if (item.role === 'assistant') {
        append(turn);
        turn = null;
      }
      continue;
    }
    append([item]);
  }
  if (turn) append(turn);
  return groups;
}

/**
 * Take the newest `requested` items, widened when needed so no unanswered
 * question is left unmounted. Returns the original array when everything
 * fits, so short threads pay nothing for this.
 */
export function resolveTranscriptWindow(messages: ChatItem[], requested: number): TranscriptWindow {
  const total = messages.length;
  if (total <= requested) return { visible: messages, hiddenCount: 0 };

  let count = requested;
  // Scan the would-be-hidden range oldest first: the first match is the
  // earliest unanswered question, and including it includes any newer one.
  for (let i = 0; i < total - requested; i++) {
    if (hasUnansweredPrompt(messages[i])) {
      count = total - i;
      break;
    }
  }

  const hiddenCount = total - count;
  return { visible: hiddenCount === 0 ? messages : messages.slice(hiddenCount), hiddenCount };
}
